import { useEffect, useMemo, useState } from "react";
import {
  HORSES,
  horseById,
  horseProgress,
  liveOrder,
  type KnPhase,
  type KnRaceCue,
  type KnRaceRecord,
} from "@ziolo/shared";
import { HorseSprite } from "./HorseSprite.js";

function raceTime(phase: KnPhase, cue: KnRaceCue | null): number {
  if (phase === "results" && cue) return 1;
  if (phase !== "racing" || !cue) return 0;
  return Math.max(0, Math.min(1, (Date.now() - cue.startedAt) / cue.durationMs));
}

export function RaceTrack({
  phase,
  cue,
  finishOrder,
  lastWinner,
}: {
  phase: KnPhase;
  cue: KnRaceCue | null;
  finishOrder: number[] | null;
  lastWinner: KnRaceRecord | null;
}) {
  const order = cue?.finishOrder ?? finishOrder ?? HORSES.map((horse) => horse.id);
  const [t, setT] = useState(() => raceTime(phase, cue));

  useEffect(() => {
    if (phase !== "racing" || !cue) {
      setT(raceTime(phase, cue));
      return;
    }
    let frame = 0;
    const tick = () => {
      setT(raceTime("racing", cue));
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [phase, cue]);

  const places = useMemo(() => {
    const running = liveOrder(t, order);
    const map = new Map<number, number>();
    running.forEach((id, index) => map.set(id, index + 1));
    return map;
  }, [t, order]);

  const leaderId = liveOrder(t, order)[0];
  const leader = leaderId !== undefined ? horseById(leaderId) : undefined;
  const racing = phase === "racing";
  const gatesOpen = racing || phase === "results";
  const winnerId = phase === "results" ? (finishOrder?.[0] ?? cue?.finishOrder[0]) : undefined;
  const call =
    phase === "waiting"
      ? "Konie w boksach"
      : phase === "betting"
        ? "Do boksów — czas na zakłady"
        : phase === "racing"
          ? t < 0.08
            ? "Start!"
            : t > 0.9
              ? "Fotofinisz!"
              : leader
                ? `${leader.name} prowadzi`
                : "Wyścig!"
          : lastWinner
            ? `Zwycięzca: ${lastWinner.winnerName}`
            : "Meta";

  return (
    <div className={`kn-track ${racing ? "kn-track--live" : ""}`} data-phase={phase}>
      <div className="kn-track__sky" />
      <div className="kn-track__crowd" aria-hidden="true" />
      <div className="kn-track__rail kn-track__rail--outer" />
      <div className="kn-surface">
        <div className="kn-lane-nums" aria-hidden="true">
          {HORSES.map((horse) => (
            <span key={horse.id} style={{ color: horse.color }}>
              {horse.id}
            </span>
          ))}
        </div>
        {HORSES.map((horse, lane) => {
          const finalRank = Math.max(0, order.indexOf(horse.id));
          const progress = horseProgress(t, horse.id, finalRank, order);
          const x = 11 + Math.min(progress, 1.12) * 78;
          const y = ((lane + 0.5) / HORSES.length) * 100;
          return (
            <div
              key={horse.id}
              className="kn-slot"
              style={{ left: `${x}%`, top: `${y}%`, zIndex: 2 + lane }}
            >
              <HorseSprite
                horse={horse}
                racing={racing}
                winner={winnerId === horse.id}
                place={racing || phase === "results" ? (places.get(horse.id) ?? null) : null}
              />
            </div>
          );
        })}
        <div className={`kn-gates ${gatesOpen ? "kn-gates--open" : ""}`}>
          {HORSES.map((horse) => (
            <div key={horse.id} className="kn-gate" style={{ background: horse.color }}>
              {horse.id}
            </div>
          ))}
        </div>
        <div className="kn-finish">
          <span>META</span>
        </div>
        <div className="kn-start-label">START</div>
      </div>
      <div className="kn-track__rail kn-track__rail--inner" />
      <div className={`kn-call ${phase === "results" ? "kn-call--win" : ""}`}>{call}</div>
      {phase === "results" && winnerId !== undefined ? (
        <div className="kn-photo">Fotofinisz</div>
      ) : null}
    </div>
  );
}
